import { ClickAwayListener, Fade, Paper, Popper } from "@mui/material";
import { useMap } from "@vis.gl/react-maplibre";
import clsx from "clsx";
import { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  AccessControls,
  AccessControlsMethodCheckEnum,
  getAliveMap,
} from "@ryde/controls";
import {
  MAP_DATA_CONTROLS_ATTR,
  onActivateKeyDown,
  PassengerMapControlsEnum,
  passengersMapAddStationSearchValueAtom,
} from "@ryde/shared";
import { ScreenDepth } from "@ryde/theme";

import { usePassengerMapSelectedControl } from "../../hooks";
import { PassengersMapAddBulkStationsPanel } from "./passengers-map-add-bulk-stations.panel";
import { PassengersMapAddStationPanel } from "./passengers-map-add-station.panel";
import { PassengersMapFilterStationsPanel } from "./passengers-map-filter-stations.panel";
import { PassengersMapLinkStationsPanel } from "./passengers-map-link-stations.panel";
import {
  AddNewStationPopperContentWrapper,
  MapButtonsControlsStyled,
} from "./passengers.map.styled";

const POPOVER_Y_OFFSET = 10;

export const MapButtonsControls: FC = () => {
  const { current: mapRef } = useMap();
  // Anchor the popper inside the map container so it is clipped and scrolled with the map, as it was on
  // Leaflet; undefined until the map exists, which MUI treats as the document body.
  const popperContainer = getAliveMap(mapRef)?.getContainer();
  const anchorElementRef = useRef<HTMLDivElement>(null);
  const [isDropdownSelectOpen, setIsDropdownSelectOpen] = useState(false);

  const setPassengersMapAddStationSearchValue = useSetRecoilState(
    passengersMapAddStationSearchValueAtom,
  );

  const {
    filterStationControl,
    addNewStationControl,
    linkStationsControl,
    handleControlClick,
    resetSelectedControl,
  } = usePassengerMapSelectedControl();

  const toggleDropdownSelect = (isOpen?: boolean): void => {
    setIsDropdownSelectOpen(isOpen ?? !isDropdownSelectOpen);
  };

  const resetAddStationControlData = useEvent(() => {
    setPassengersMapAddStationSearchValue(undefined);
    resetSelectedControl();
  });

  const { t } = useTranslation();

  return (
    <div
      {...{ [MAP_DATA_CONTROLS_ATTR]: true }}
      onClick={e => e.stopPropagation()}
      role="presentation"
    >
      <MapButtonsControlsStyled>
        <span
          onClick={() =>
            handleControlClick(PassengerMapControlsEnum.filterStations)
          }
          onKeyDown={onActivateKeyDown(() =>
            handleControlClick(PassengerMapControlsEnum.filterStations),
          )}
          role="button"
          tabIndex={0}
          aria-label={t("station.filterStations")}
          className={clsx("mapControlBtn", {
            selected: filterStationControl.selected,
            disabled: filterStationControl.disabled,
          })}
        >
          <SVG_MUI.FlagSearch4SVG />
        </span>

        <span
          onClick={() =>
            addNewStationControl.selected
              ? resetAddStationControlData()
              : toggleDropdownSelect()
          }
          onKeyDown={onActivateKeyDown(() =>
            addNewStationControl.selected
              ? resetAddStationControlData()
              : toggleDropdownSelect(),
          )}
          role="button"
          tabIndex={0}
          aria-label={t("station.addStation")}
          className={clsx("mapControlBtn", {
            selected: addNewStationControl.selected,
            disabled: addNewStationControl.disabled,
          })}
          ref={anchorElementRef}
        >
          <SVG_MUI.LocationSidePlus4SVG />
        </span>
        <span
          onClick={() =>
            handleControlClick(PassengerMapControlsEnum.linkStations)
          }
          onKeyDown={onActivateKeyDown(() =>
            handleControlClick(PassengerMapControlsEnum.linkStations),
          )}
          role="button"
          tabIndex={0}
          aria-label={t("station.linkStations")}
          className={clsx("mapControlBtn", {
            selected: linkStationsControl.selected,
            disabled: linkStationsControl.disabled,
          })}
        >
          <SVG_MUI.Link4SVG />
        </span>
      </MapButtonsControlsStyled>

      <PassengersMapFilterStationsPanel
        handleClose={resetSelectedControl}
        isOpen={filterStationControl.selected}
      />

      <PassengersMapLinkStationsPanel
        handleClose={resetSelectedControl}
        isOpen={linkStationsControl.selected}
      />

      <PassengersMapAddStationPanel
        handleClose={resetAddStationControlData}
        isOpen={
          addNewStationControl.selectedPanel ===
          PassengerMapControlsEnum.addOneNewStation
        }
      />

      <PassengersMapAddBulkStationsPanel
        handleClose={resetSelectedControl}
        isOpen={
          addNewStationControl.selectedPanel ===
          PassengerMapControlsEnum.addBulkNewStation
        }
      />

      <Popper
        anchorEl={anchorElementRef.current}
        open={isDropdownSelectOpen}
        container={popperContainer}
        placement={"left-start"}
        style={{ zIndex: ScreenDepth.MAP_ATTRIBUTIONS }}
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, POPOVER_Y_OFFSET],
              },
            },
          ],
        }}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper onClick={() => toggleDropdownSelect(false)}>
              <ClickAwayListener
                onClickAway={() => toggleDropdownSelect(false)}
              >
                <AddNewStationPopperContentWrapper>
                  <AccessControls
                    actionsList={[
                      { action: "create", entity: "station" },
                      { action: "update", entity: "passenger" },
                    ]}
                    methodCheck={AccessControlsMethodCheckEnum.AND}
                  >
                    <div
                      className="add-station-control-option-item"
                      onClick={() =>
                        handleControlClick(
                          PassengerMapControlsEnum.addOneNewStation,
                        )
                      }
                      onKeyDown={onActivateKeyDown(() =>
                        handleControlClick(
                          PassengerMapControlsEnum.addOneNewStation,
                        ),
                      )}
                      role="button"
                      tabIndex={0}
                    >
                      {t("station.addOneStation")}
                    </div>

                    <div
                      className="add-station-control-option-item"
                      onClick={() =>
                        handleControlClick(
                          PassengerMapControlsEnum.addBulkNewStation,
                        )
                      }
                      onKeyDown={onActivateKeyDown(() =>
                        handleControlClick(
                          PassengerMapControlsEnum.addBulkNewStation,
                        ),
                      )}
                      role="button"
                      tabIndex={0}
                    >
                      {t("station.bulkStationAddition")}
                    </div>
                  </AccessControls>
                </AddNewStationPopperContentWrapper>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </div>
  );
};
